import { Link } from "@inertiajs/react";
import { useForm } from "@inertiajs/react";
import { Clock, BookOpen } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import MinutesField from "@/components/forms/MinutesField";
import { formatMinutesDuration } from "@/lib/minutes";

function LogAtHomePreview() {
  const { data, setData } = useForm({ minutes: "20", note: "Charlotte's Web · ch 3" });
  const minutesLabel = formatMinutesDuration(data.minutes) || "?";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          How long did Sarah read?
        </CardTitle>
        <CardDescription>One quick log from your phone, anytime.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5">
          <MinutesField name="minutes" required value={data.minutes} onChange={(e) => setData("minutes", e.target.value)} />
          <FormField name="note" label="Note (optional)" value={data.note} onChange={(e) => setData("note", e.target.value)} placeholder="What did they read?" />
          <Button type="submit" size="lg">
            <Clock className="h-4 w-4" />
            Log {minutesLabel}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function LoggingMinutesAtHome() {
  return (
    <>
      <p>
        Bedtime stories count. Reading on the couch counts. Anything reading-related counts. Logging it takes about 10 seconds.
      </p>

      <h2>How to log</h2>
      <p>
        On your child's card, tap <strong>Log minutes</strong>.
      </p>
      <Screenshot caption="Logging from your phone">
        <div className="max-w-md mx-auto">
          <LogAtHomePreview />
        </div>
      </Screenshot>

      <p>
        Enter hours, minutes, or both, add a quick note about what they read if you'd like, hit log. Done.
      </p>

      <Callout variant="tip" title="Round numbers are fine">
        <p>
          If they read for 22 minutes, log 20 — close enough. The point is encouragement, not a precise tally. Schools care about kids reading more, not about minute-by-minute accuracy.
        </p>
      </Callout>

      <h2>What counts</h2>
      <p>
        Whatever your school says counts. Generally that means any reading your child does, on their own or with you: bedtime, the bus ride, reading recipes together, comics, audiobooks if the school's okay with them. If you're unsure, ask the teacher.
      </p>

      <h2>Made a mistake?</h2>
      <p>
        On your child's page, your recent logs each have an undo button. You've got 24 hours to undo and re-log. After that, just message the teacher — they can fix it.
      </p>

      <p>
        If you don't see the log button, it usually means the campaign hasn't started yet, or it's already wrapped up. Reading minutes only count while the read-a-thon is running.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/setting-personal-goals">Setting a personal goal</Link>.</li>
        <li><Link href="/help/articles/sharing-with-family">Sharing with family</Link>.</li>
      </ul>
    </>
  );
}
